import requestWithAuth from "services/httpClient";
import { rootURL } from "services/rootURL";

export type Schedule = {
	_id: string;
	startTime: string;
	endTime: string;
	translatorId: string;
	clients: string[];
	isActive?: boolean;
};

const API_URL = `${rootURL}schedule`;

export const getSchedulesRequest = async () => {
	const res = await requestWithAuth<Schedule[]>("get", `${API_URL}/get`);
	if (res.ok) {
		return res.body;
	}
	throw new Error("Something went wrong with schedules data");
};

export const createScheduleRequest = async (
	scheduleData: Omit<Schedule, "_id">,
) => {
	const res = await requestWithAuth("post", API_URL).send(scheduleData);
	if (res.ok) {
		return res.body;
	}
	throw new Error("Failed to create schedule");
};

export const updateScheduleRequest = async (
	scheduleId: string,
	updatedData: Partial<Schedule>,
) => {
	return await requestWithAuth("put", `${API_URL}/${scheduleId}`).send(
		updatedData,
	);
};

export const deleteScheduleRequest = async (id: string) => {
	return await requestWithAuth("delete", `${API_URL}/${id}`);
};

export const getScheduleByIdRequest = async (id: string) => {
	const res = await requestWithAuth<Schedule>("get", `${API_URL}/get/${id}`);
	if (res.ok) {
		return res.body;
	}
	throw new Error("Failed to get schedule");
};

export type TranslatorLite = {
	_id: string;
	name: string;
	surname: string;
	suspended?: { status?: boolean } | undefined;
	email?: string;
	wantsToReceiveEmails?: boolean;
};

export type ClientLite = {
	_id: string;
	name: string;
	surname: string;
	translators: Array<{ _id: string }>;
};

export type OverridesSummary = {
	scheduleId: string;
	perFieldCounts: Record<string, number>;
	byClient: Array<{ clientId: string; fields: string[] }>;
};

export type SchedulesPageData = {
	schedules: Array<
		Pick<
			Schedule,
			"_id" | "startTime" | "endTime" | "translatorId" | "clients" | "isActive"
		>
	>;
	translators: Array<TranslatorLite>;
	clients: Array<ClientLite>;
	overridesSummary?: OverridesSummary[];
};

export const getSchedulesPageDataRequest = async () => {
	const res = await requestWithAuth("get", `${API_URL}/page-data`);
	if (res.ok) {
		return res.body as SchedulesPageData;
	}
	throw new Error("Failed to fetch schedules page data");
};

export const getActiveTranslatorIdsRequest = async (): Promise<string[]> => {
	const res = await requestWithAuth<{ activeTranslatorIds?: string[] }>(
		"get",
		`${API_URL}/active-translators`,
	);
	if (res.ok) {
		return res.body?.activeTranslatorIds ?? [];
	}
	throw new Error("Failed to fetch active translator ids");
};

export type ClientIdleWindow = {
	clientId: string;
	idle: Array<{ start: string; end: string }>;
};

export const getClientIdleWindowsRequest = async (
	timezone = "Europe/Kiev",
): Promise<{ timezone: string; data: ClientIdleWindow[] }> => {
	const res = await requestWithAuth(
		"get",
		`${API_URL}/client-idle?timezone=${encodeURIComponent(timezone)}`,
	);
	if (res.ok) {
		return res.body as { timezone: string; data: ClientIdleWindow[] };
	}
	throw new Error("Failed to fetch client idle windows");
};
